import { useState, useCallback, useEffect } from 'react'
import _ from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import errorHelper, { httpHandler } from '../../../../../lib/errorHelper'
import safeAwait from '../../../../../lib/safeAwait'
import { getAPIFormatDate, getRangeAPIDate } from '../../../../lib/MomentHelper'
import { numeralFormat, dollarFormat } from '../../../../../lib/renderUtil'
import { showOnlyNumber } from '../../../../lib/formatter'
import { EMPTY_VALUE } from '../../../../constants/globalTypes'
import { Icon, Button, Loader, Modal, Tooltip, Toast } from '@vzmi/shp_shared_compo'
import { Card, Input, Col, Row, Typography } from 'antd'
import { ConnectedListingLink } from '../../../Common/ListingLink/ListingLink'
import { HyperLink } from '../../../Common/Form'
import { Error } from '../../../../components/Common/StatePage'
import { getListingImage, getAvailableCountLink } from '../Shared'
import styles from './CustomFollow.css'
const { Paragraph } = Typography

const i13nModel = {
  action: 'center_supplierDashboard',
  category: 'supplierCenter',
  label: 'undefined'
}
export const CUSTOM_FOLLOW_TITLE = '自訂追蹤賣場'

export const sortModifiedTsData = (data) => {
  return _.sortBy(_.values(data), 'modifiedTs')
}

export const transformToCardData = (payload = {}) => {
  const {
    trackListingsModifiedTs = [],
    listings = [],
    returns = [],
    bestsellers = [],
    listingTags = []
  } = payload
  const listingMap = _.keyBy(listings, 'id')
  const returnMap = _.keyBy(returns, 'id')
  const listingTagMap = _.keyBy(listingTags, 'listingId')
  const bestsellerMap = _.keyBy(bestsellers, 'id')
  const result = trackListingsModifiedTs.map((trackListing) => {
    const { id: listingId } = trackListing
    const listingInfo = _.get(listingMap, listingId, {})
    const returnInfo = _.get(returnMap, listingId, {})
    const listingTagInfo = _.get(listingTagMap, listingId, {})
    const bestsellerInfo = _.get(bestsellerMap, listingId, {})

    return {
      trackListingInfo: trackListing,
      listingInfo,
      returnInfo,
      listingTagInfo,
      bestsellerInfo,
      image: _.get(listingInfo, 'image')
    }
  })
  return result
}

export const getUnitConversionData = (value, isDollar = false) => {
  if (isDollar) {
    return dollarFormat(showOnlyNumber(value)) || EMPTY_VALUE
  } else {
    return numeralFormat(value, { allowZero: true })
  }
}

// 取得追蹤賣場 Data
export const getTrackListingsData = async (req) => {
  const params = {
    status: 'enabled',
    limit: 12
  }

  const [error, payload = []] = await safeAwait(req(params))
  const errorMessage = errorHelper.parseErrorMessage(error)
  if (errorMessage) {
    return [{ message: errorMessage, params }]
  }

  const trackListings = _.get(payload, 'trackListings', [])
  const trackListingsModifiedTs = _.map(trackListings, (item) => {
    return {
      id: _.toString(item.listingId),
      modifiedTs: item.modifiedTs
    }
  })
  // 組新的 trackListingsIdData
  const trackListingIds = _.map(trackListings, trackListing => trackListing.listingId)
  return [undefined, trackListingIds, trackListingsModifiedTs]
}

// 取得賣場 Data
export const getListingsData = async (ids, req) => {
  const params = {
    id: ids,
    limit: 50,
    offset: 0,
    page: 1,
    fields: ''
  }

  const [error, payload = []] = await safeAwait(req(params))
  const errorMessage = errorHelper.parseErrorMessage(error)
  if (errorMessage) {
    return [{ message: errorMessage, params }]
  }

  const listings = _.get(payload, 'listings', [])
  // 組新的 listingsData
  const newListings = _.map(listings, (listing) => {
    const thumbnail = getListingImage(listing)
    return {
      id: _.toString(listing.id),
      name: listing.name,
      price: listing.price,
      availableCount: listing.availableCount,
      image: thumbnail,
      startTs: listing.startTs,
      shipType: listing.skuDetail.shipType,
      sku: listing.sku,
      skuSubStationId: listing.skuSubStationId
    }
  })
  return [undefined, newListings]
}

// 取得熱門賣場 Data
export const getBestsellersData = async (ids, req) => {
  const range = [moment().subtract(7, 'days'), moment().subtract(1, 'days')]
  const params = {
    listingIds: ids,
    limit: 50,
    offset: 0,
    // 要帶utc格式
    reportDate: getRangeAPIDate(range)
  }

  const [error, payload = []] = await safeAwait(req(params))
  const errorMessage = errorHelper.parseErrorMessage(error)
  if (errorMessage) {
    return [{ message: errorMessage, params }]
  }

  const bestsellers = _.get(payload, 'bestsellers', [])
  // 組新的 bestsellersData
  const newBestsellers = _.map(bestsellers, (bestseller) => {
    return {
      id: bestseller.listingId,
      soldQuantity: bestseller.soldQuantity,
      soldAmount: bestseller.soldAmount,
      pageView: bestseller.pageView
    }
  })
  return [undefined, newBestsellers]
}

// 取得退貨分析 Data
export const getReturnsData = async (ids, req) => {
  const params = {
    listingIds: ids,
    fields: '+listings',
    // 資料統計區間範例：2023-07-11 ~ 2023-07-17(昨日)
    reportDateStart: getAPIFormatDate(moment().subtract(7, 'days')),
    reportDateEnd: getAPIFormatDate(moment().subtract(1, 'days'))
  }

  const [error, payload = []] = await safeAwait(req(params))
  const errorMessage = errorHelper.parseErrorMessage(error)
  if (errorMessage) {
    return [{ message: errorMessage, params }]
  }

  const listings = _.get(payload, 'listings', [])
  // 組新的 returnsData
  const newReturns = _.map(listings, (listing) => {
    return {
      id: listing.id,
      returnedAmount: listing.returnedAmount,
      returnedQuantity: listing.returnedQuantity
    }
  })
  return [undefined, newReturns]
}

// 取得收藏數 Data
export const getListingTagsData = async (ids, req) => {
  const params = {
    listingIds: ids
  }

  const [error, payload = []] = await safeAwait(req(params))
  const errorMessage = errorHelper.parseErrorMessage(error)
  if (errorMessage) {
    return [{ message: errorMessage, params }]
  }

  const listingTags = _.get(payload, 'listingTags', [])
  return [undefined, listingTags]
}

export const fetchCardData = async (ids, actions, setIsError, setIsLoading) => {
  const { fetchListings, fetchPaginationBestsellers, fetchReturns, fetchListingTags } = actions
  const listingsReq = params => fetchListings(params).then(httpHandler)
  const bestsellersReq = params => fetchPaginationBestsellers(params).then(httpHandler)
  const returnsReq = params => fetchReturns(params).then(httpHandler)
  const listingTagsReq = params => fetchListingTags(params).then(httpHandler)
  const [
    [listingsError, listings = []],
    [bestsellersError, bestsellers = []],
    [returnsError, returns = []],
    [listingTagsError, listingTags = []]
  ] = await Promise.all([
    getListingsData(ids, listingsReq),
    getBestsellersData(ids, bestsellersReq),
    getReturnsData(ids, returnsReq),
    getListingTagsData(ids, listingTagsReq)
  ])

  const errorMsg = []
  if (listingsError) {
    errorMsg.push({
      title: '取得賣場資料失敗：',
      error: listingsError
    })
  }

  if (bestsellersError) {
    errorMsg.push({
      title: '取得熱門賣場失敗：',
      error: bestsellersError
    })
  }

  if (returnsError) {
    errorMsg.push({
      title: '取得退貨資料失敗：',
      error: returnsError
    })
  }

  if (listingTagsError) {
    errorMsg.push({
      title: '取得收藏數失敗：',
      error: listingTagsError
    })
  }

  if (errorMsg.length > 0) {
    // 追蹤賣場為空時，不應提醒
    if (!_.isEmpty(ids)) {
      _.forEach(errorMsg, element => {
        console.error(element.title, element.error)
      })
    }
    setIsError(true)
    setIsLoading(false)
    return {}
  }

  return { listings, bestsellers, returns, listingTags }
}

export const updateTrackListingsData = async (id, req) => {
  const params = {
    listingId: id,
    status: 'disabled'
  }

  const [error, payload = []] = await safeAwait(req(params))
  const errorMessage = errorHelper.parseErrorMessage(error)
  if (errorMessage) {
    return [{ message: errorMessage, params }]
  }
  return [undefined, payload]
}

export const createTrackListingsData = async (id, req) => {
  const params = {
    listingId: id
  }

  const [error, payload = []] = await safeAwait(req(params))
  const errorMessage = errorHelper.parseErrorMessage(error)
  if (errorMessage) {
    return [{ message: errorMessage, params }]
  }
  const trackListingModifiedTs = [{
    id: _.toString(payload.listingId),
    modifiedTs: payload.modifiedTs
  }]

  return [undefined, trackListingModifiedTs]
}

const FollowInput = props => {
  const { originListingsData, followInputVal, setFollowInputVal, createTrackListings, setIsLoading, setIsError, setListingsData, actions } = props

  const onSubmitCreateTrack = async () => {
    setIsLoading(true)
    const req = params => createTrackListings(params).then(httpHandler)
    const [resError, trackListingsModifiedTs = []] = await createTrackListingsData(followInputVal, req)
    if (resError) {
      Toast.error(`${resError.message}`)
      console.error(resError)
    } else {
      const data = await fetchCardData([followInputVal], actions, setIsError, setIsLoading)
      if (!_.isEmpty(data)) {
        const payload = {
          ...data,
          trackListingsModifiedTs
        }
        const newListingsData = transformToCardData(payload)
        const sortedData = sortModifiedTsData([...originListingsData, ...newListingsData])
        // 更新頁面資料
        setListingsData(sortedData)
        Toast.success('新增成功')
        props.sendDashboardPageview({ ...i13nModel, label: 'customFollow_addlisting' })
      } else {
        Toast.error('取得追蹤賣場小卡資料失敗')
      }
    }
    setFollowInputVal('')
    setIsLoading(false)
  }

  return (
    <Card className={styles.card}>
      <Row>
        <Col span={24}>
          <h1>請輸入欲追蹤的賣場編號</h1>
        </Col>
      </Row>
      <Row>
        <Col
          span={24}
          className={styles.spacing}
        >
          <Input
            allowClear
            value={followInputVal}
            onChange={(e) => {
              setFollowInputVal(e.target.value)
            }}
          />
        </Col>
      </Row>
      <Row justify='end'>
        <Col
          className={styles.spacing}
        >
          <Button onClick={onSubmitCreateTrack}>
            確認
          </Button>
        </Col>
      </Row>
    </Card>
  )
}

const StatsContainer = props => {
  const { fetchListings, fetchPaginationBestsellers, fetchListingTags, fetchReturns, fetchTrackListings, createTrackListings, updateTrackListings, page, sendDashboardPageview } = props
  const isPM = _.get(page, 'user.identity.isPM', false)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [cancelFollowStatus, setCancelFollowStatus] = useState({
    id: '',
    status: false
  })
  const [listingsData, setListingsData] = useState([])
  const [followInputVal, setFollowInputVal] = useState('')

  // 移除追蹤
  const handleCancleFollow = (id) => {
    setCancelFollowStatus({
      id,
      status: true
    })
    sendDashboardPageview({ ...i13nModel, label: 'customFollow_removelisting' })
  }

  const onRemoveTrackListing = async () => {
    setCancelFollowStatus({ status: false })
    setIsLoading(true)
    const { id } = cancelFollowStatus
    const req = params => updateTrackListings(params).then(httpHandler)
    const [resError] = await updateTrackListingsData(id, req)
    if (resError) {
      Toast.error('移除失敗請，稍後再試')
      console.error(resError)
    } else {
      const newListingsData = _.filter(listingsData, (item) => {
        return _.get(item, 'trackListingInfo.id') !== id
      })
      const sortedData = sortModifiedTsData(newListingsData)
      setListingsData(sortedData)
      Toast.success('移除成功')
    }
    setIsLoading(false)
  }

  const onCancel = () => setCancelFollowStatus({ status: false })

  const search = useCallback(() => {
    const fetchData = async () => {
      setIsLoading(true)
      const req = params => fetchTrackListings(params).then(httpHandler)
      const [trackListingsError, trackListingIds = [], trackListingsModifiedTs = []] = await getTrackListingsData(req)
      if (trackListingsError) {
        setIsError(true)
        Toast.error(`取得追蹤賣場資料失敗：${trackListingsError.message}`)
        console.error(trackListingsError)
        return
      } else {
        if (!_.isEmpty(trackListingIds)) {
          const actions = {
            fetchListings,
            fetchPaginationBestsellers,
            fetchListingTags,
            fetchReturns
          }
          const data = await fetchCardData(trackListingIds, actions, setIsError, setIsLoading)
          if (!_.isEmpty(data)) {
            const payload = {
              ...data,
              trackListingsModifiedTs
            }
            const newListingsData = transformToCardData(payload)
            setListingsData(newListingsData)
            setIsLoading(false)
          } else {
            Toast.error('取得追蹤賣場小卡資料失敗')
            console.error('fetchCardData returned empty data due to errors')
          }
        }
      }
      setIsLoading(false)
    }
    fetchData()
  }, [
    fetchTrackListings,
    fetchListings,
    fetchPaginationBestsellers,
    fetchListingTags,
    fetchReturns
  ])

  useEffect(() => {
    search()
  }, [search])

  return (
    <Loader loaded={!isLoading}>
      {
        isError
          ? (
            <Error
              onClick={search}
              iconType='update'
              text='系統忙碌中，請稍後再試'
            />
          )
          : (
            <>
              <div className={styles.info}>
                <Icon
                  name='ec-information-fill'
                  align='sub'
                  color='#979ba7'
                  size={17}
                />
                可設定最多 12 組欲追蹤之賣場，以便觀測近期重點商品狀況。
              </div>
              <div>
                <Row gutter={[10, 10]}>
                  {_.map(listingsData, (item, index) => {
                    const { image } = item
                    const {
                      sku: productId,
                      skuSubStationId,
                      shipType = {},
                      name,
                      price,
                      availableCount
                    } = _.get(item, 'listingInfo', {})
                    const {
                      soldQuantity,
                      soldAmount,
                      pageView
                    } = _.get(item, 'bestsellerInfo', {})
                    const {
                      returnedQuantity,
                      returnedAmount
                    } = _.get(item, 'returnInfo', {})
                    const {
                      abandonedCartQuantity,
                      tagQuantity
                    } = _.get(item, 'listingTagInfo', {})
                    const {
                      id: listingId = ''
                    } = _.get(item, 'trackListingInfo', {})
                    const { sharedLink } = props
                    const subNo = _.replace(skuSubStationId, 'sub', '')
                    const params = {
                      productIds: productId,
                      subNo
                    }
                    const query = `?${queryString.stringify(params)}`
                    // 目前售價
                    const priceLink = `${_.get(props, 'sharedLink.scmProductCostPrice')}${query}`
                    // 可訂購量
                    const availableCountLink = getAvailableCountLink(sharedLink, shipType, listingId)
                    return (
                      <Col
                        span={8}
                        key={index}
                      >
                        <Card className={styles.card}>
                          <Row>
                            <Col span={12}>
                              <img src={image} />
                            </Col>
                            <Col span={12}>
                              <ConnectedListingLink targetListingId={listingId}>
                                {listingId}
                              </ConnectedListingLink>
                              <Tooltip title={name}>
                                <Paragraph
                                  strong
                                  ellipsis={{ rows: 1 }}
                                >
                                  {name}
                                </Paragraph>
                              </Tooltip>
                              <p>
                                目前售價：
                                <HyperLink
                                  target='_blank'
                                  href={priceLink}
                                  onClick={() => {
                                    sendDashboardPageview({
                                      ...i13nModel,
                                      label: 'customFollow_currentprice'
                                    })
                                  }}
                                >
                                  {getUnitConversionData(price, true)}
                                </HyperLink>
                              </p>
                              <p>
                                可訂購量：
                                <HyperLink
                                  target='_blank'
                                  href={availableCountLink}
                                  onClick={() => {
                                    sendDashboardPageview({
                                      ...i13nModel,
                                      label: 'customFollow_availcnt'
                                    })
                                  }}
                                >
                                  {getUnitConversionData(availableCount, false)}
                                </HyperLink>
                              </p>
                            </Col>
                          </Row>
                          <Row className={styles.spacing}>
                            <Col span={24}>
                              <h1>前7日賣場銷售與狀況</h1>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={12}>
                              <p>
                                訂購量：
                                {getUnitConversionData(soldQuantity, false)}
                              </p>
                              <p>
                                退貨量：
                                {getUnitConversionData(returnedQuantity, false)}
                              </p>
                              <p>
                                瀏覽量：
                                {getUnitConversionData(pageView, false)}
                              </p>
                            </Col>
                            <Col span={12}>
                              <p>
                                訂購金額：
                                {getUnitConversionData(soldAmount, true)}
                              </p>
                              <p>
                                退貨金額：
                                {getUnitConversionData(returnedAmount, true)}
                              </p>
                            </Col>
                          </Row>
                          <Row className={styles.spacing}>
                            <Col span={24}>
                              <h1>累積至昨日買家有興趣</h1>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={12}>
                              <p>
                                未結帳數：
                                {getUnitConversionData(abandonedCartQuantity, false)}
                              </p>
                            </Col>
                            <Col span={12}>
                              <p>
                                加入收藏數：
                                {getUnitConversionData(tagQuantity, false)}
                              </p>
                            </Col>
                          </Row>
                          <Row className={styles.spacing}>
                            {!isPM && (
                              <Col span={12}>
                                <Button
                                  className={styles.btn}
                                  buttonType='outline'
                                  size='small'
                                  onClick={() => {
                                    handleCancleFollow(listingId)
                                  }}
                                >
                                  移除追蹤
                                </Button>
                              </Col>
                            )}
                            <Col span={12}>
                              <Button
                                className={styles.btn}
                                buttonType='outline'
                                size='small'
                              >
                                <HyperLink
                                  target='_blank'
                                  href={`/listing/query?id=${listingId}&idType=listing&page=1`}
                                  onClick={() => {
                                    sendDashboardPageview({
                                      ...i13nModel,
                                      label: 'customFollow_modifylisting'
                                    })
                                  }}
                                >
                                  異動賣場
                                </HyperLink>
                              </Button>
                            </Col>
                            {/* 此功能尚未開發 */}
                            {/* <Col span={8}>
                              <Button
                                className={styles.btn}
                                size='small'
                                buttonType='outline'
                              >
                                發折價卷
                              </Button>
                            </Col> */}
                          </Row>
                        </Card>
                      </Col>
                    )
                  })}
                  {(listingsData.length < 12 && !isPM) && (
                    <Col span={8}>
                      <FollowInput
                        key='followInput'
                        originListingsData={listingsData}
                        followInputVal={followInputVal}
                        setFollowInputVal={setFollowInputVal}
                        createTrackListings={createTrackListings}
                        setIsLoading={setIsLoading}
                        setIsError={setIsError}
                        setListingsData={setListingsData}
                        actions={{
                          fetchListings,
                          fetchPaginationBestsellers,
                          fetchReturns,
                          fetchListingTags
                        }}
                        sendDashboardPageview={sendDashboardPageview}
                      />
                    </Col>
                  )}
                </Row>
              </div>
              {/* 移除追蹤 */}
              {
                cancelFollowStatus.status && (
                  <Modal
                    className={styles.modal}
                    visible={cancelFollowStatus.status}
                    onOk={onRemoveTrackListing}
                    onCancel={onCancel}
                    okText='確定移除'
                    title={`確定取消追蹤 賣場 ${cancelFollowStatus.id} ？`}
                  />
                )
              }
            </>
          )
      }
    </Loader>
  )
}

export default StatsContainer
